import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useAtomValue, useSetAtom } from 'jotai';

import { Modal, ModalConfirmContent, ModalContent } from '@restworld/ui-ds-base';

import { inAppAssessmentConfirmationAtom, inAppInterviewModalJobPositionIdAtom } from '../worker-entry-point.atoms';
import { useInterviewCountdown } from './use-interview-countdown';
import { reportInterviewHrefMissing, reportInterviewNavigationFailed } from './in-app-assessment-observability';

/**
 * The atom flip below decides the parent's chrome, so it has to land *before* the first paint,
 * not after it: with a plain `useEffect`, `WorkerEntryPointModal` reads the previous value and
 * paints Modal B once with the close X, Escape and the backdrop still live. The window is
 * sub-frame and not realistically clickable, but it is the exact guarantee this component is
 * built around, and "not realistically" is not the same as "not".
 *
 * `useLayoutEffect` warns when React renders on the server, and these modals are client-only,
 * so the usual guard: the layout effect where there is a document, the plain one where there
 * is not. Same shape the dependencies in this tree use.
 */
const useBeforePaintEffect = typeof document !== 'undefined' ? useLayoutEffect : useEffect;

/** The chat for this application. Addressed by job position, like the rest of `/candidature`. */
export const preInterviewHref = (jobPositionId?: string) => `/candidature/${jobPositionId}/intervista`;

/**
 * Modal A, Andrea's slide 6: the worker who had no account when the application flow started
 * and whose assessment runs in the app. It keeps the close X (the parent's default) and offers
 * two ways on, because this worker is not being sent into an interview right now.
 *
 * Rendered from the end of the CV upload flow, which is what "had no account at the start"
 * means in this codebase: the already-authenticated worker never reaches that screen.
 */
export const InAppAssessmentNewConfirmation = () => {
  const { t } = useTranslation('onboarding-page');
  const router = useRouter();
  const setConfirmation = useSetAtom(inAppAssessmentConfirmationAtom);

  // Clearing on unmount is what stops the NEXT modal from opening without its footer: the
  // parent reads this atom to decide its chrome, and nothing else resets it.
  useBeforePaintEffect(() => {
    setConfirmation('new');
    return () => {
      setConfirmation(null);
    };
  }, [setConfirmation]);

  return (
    <ModalConfirmContent
      title={t('in_app_assessment_application_sent')}
      subtitle={t('in_app_assessment_new_subtitle')}
      primaryAction={{
        label: t('go_to_your_profile'),
        onClick: () => router.push('/profilo'),
      }}
      secondaryAction={{
        label: t('search_other_jobs'),
        onClick: () => router.push('/cerco-lavoro'),
      }}
    />
  );
};

/**
 * Modal B, Andrea's slide 7: the worker who was already signed in when the flow started and
 * has done this before. One way on, into the interview, and a ten second countdown that takes
 * them there by itself.
 *
 * It has no close X. That is Andrea's design (decision 106), and `WorkerEntryPointModal`
 * applies it by reading `inAppAssessmentConfirmationAtom`.
 *
 * **Escape still closes it, and that is accepted** (Edoardo, decision 126). An earlier
 * version of this comment claimed Escape and the backdrop were closed off too. They are not:
 * `disableOverlayClose`, the only prop the parent passes for this, never touches Escape, and
 * on the desktop branch of `libs/ui-ds-base/src/lib/modal/modal.tsx` it only puts a
 * `stopPropagation` on the overlay's own `onClick`. Measured rather than deduced: mounting
 * that Modal with `disableOverlayClose` and firing Escape calls `onOpenChange(false)`.
 *
 * So the honest description is: no X, Escape closes, and the backdrop is **unverified**. The
 * jsdom probe saw it not close, but jsdom does not faithfully reproduce the document-level
 * outside-pointer-down detection Radix uses, so that is not evidence either way and wants a
 * real browser. The worker who presses Escape lands on the ordinary confirmation with no
 * chat, which is a smaller cost than changing a shared design-system component two days
 * before a release.
 */
export const InAppAssessmentReturningConfirmation = ({ jobPositionId }: { jobPositionId?: string }) => {
  const { t } = useTranslation('onboarding-page');
  const router = useRouter();
  const setConfirmation = useSetAtom(inAppAssessmentConfirmationAtom);

  /**
   * One navigation at most, and a way out if it does not happen.
   *
   * The guard is inside here rather than in the click handler because the handler is not the
   * only caller: a double click, or a click landing after the countdown has already called
   * `router.push` but before the route tears this component down, would otherwise navigate
   * twice. Stopping the interval does not prevent that; this does.
   *
   * The failure branch is the one that matters. `router.push` does not only throw: it resolves
   * `false` when Next cancels the navigation, which is silent. On a modal with no X, no Escape
   * and no backdrop, a silent failure would leave the worker with nothing but the button that
   * just failed.
   *
   * So a failed client-side navigation falls back to a **full page load** of the same address
   * (Edoardo, decision 122). The modal stays as designed, with no way out, because the way out
   * is the chat itself: the worker gets there either way, and a browser navigation does not
   * depend on whatever made the router give up. The close controls are deliberately **not**
   * restored — an earlier version did that, and it traded the worker's destination for an
   * escape hatch they did not need.
   *
   * The report stays, because the point of it is to count how often the router gives up.
   */
  const navigatedRef = useRef(false);

  const onNavigationFailed = useCallback(
    (outcome: 'threw' | 'rejected' | 'cancelled', trigger: 'countdown' | 'button', error: unknown) => {
      reportInterviewNavigationFailed(error, { jobPositionId, trigger, outcome });
      // A full load of the same address, not a second router attempt: whatever stopped the
      // client-side navigation would stop it again. `navigatedRef` stays set, so this runs once.
      window.location.assign(preInterviewHref(jobPositionId));
    },
    [jobPositionId]
  );

  const navigate = useCallback(
    (trigger: 'countdown' | 'button') => {
      if (navigatedRef.current) return;
      navigatedRef.current = true;

      const href = preInterviewHref(jobPositionId);
      try {
        Promise.resolve(router.push(href)).then(
          (ok) => {
            // `false` is Next cancelling the route. It throws nothing, so without this check
            // the worker is simply stuck.
            if (ok === false)
              onNavigationFailed('cancelled', trigger, new Error(`Navigation to ${href} was cancelled`));
          },
          (error: unknown) => onNavigationFailed('rejected', trigger, error)
        );
      } catch (error) {
        onNavigationFailed('threw', trigger, error);
      }
    },
    [router, jobPositionId, onNavigationFailed]
  );

  const goToInterview = useCallback(() => navigate('countdown'), [navigate]);

  const { remaining, stop } = useInterviewCountdown(goToInterview);

  const onStartNow = useCallback(() => {
    // Order matters: stop the clock first, so the navigation below cannot be followed by the
    // timer's own a moment later.
    stop();
    navigate('button');
  }, [stop, navigate]);

  // Drawn with nowhere to go: the single button would lead to `/candidature/undefined/...`.
  useEffect(() => {
    if (!jobPositionId) reportInterviewHrefMissing();
  }, [jobPositionId]);

  useBeforePaintEffect(() => {
    setConfirmation('returning');
    return () => {
      setConfirmation(null);
    };
  }, [setConfirmation]);

  return (
    <ModalConfirmContent
      title={t('in_app_assessment_application_sent')}
      subtitle={t('in_app_assessment_returning_subtitle')}
      primaryAction={{
        label: t('start_the_interview'),
        onClick: onStartNow,
      }}
    >
      {/* The countdown has to be visible: a modal with no way out that moves on its own after
          ten silent seconds is a trap rather than a shortcut. */}
      <p className="text-base text-gray-500" data-cy="interview-countdown">
        {t('interview_starts_in_seconds', { count: remaining })}
      </p>
    </ModalConfirmContent>
  );
};

/**
 * Modal B on its own, for the paths that end an application outside `WorkerEntryPointModal`:
 * the phone verification (OTP) on the position page and the end of the manual wizard on
 * `/profilo`. Both used to open the confirmation that says Serena will write on WhatsApp,
 * whatever the channel (PR #398 and the `?manage_using_klaaryo=true` redirect).
 *
 * Same chrome as Modal B inside the entry point: no close X, no backdrop or Escape dismissal.
 * The way out is the chat, by the button or by the countdown.
 */
export const InAppInterviewConfirmationModal = () => {
  const jobPositionId = useAtomValue(inAppInterviewModalJobPositionIdAtom);
  const setJobPositionId = useSetAtom(inAppInterviewModalJobPositionIdAtom);

  // Cleared when the page that hosts it goes away (the navigation into the chat), so a back
  // navigation lands on the page without the modal and its countdown.
  useEffect(() => () => setJobPositionId(null), [setJobPositionId]);

  return (
    <Modal open={!!jobPositionId} disableOverlayClose onOpenChange={() => undefined}>
      <ModalContent showCloseIcon={false} className="min-h-[20rem] max-w-full w-full md:max-w-lg md:min-w-104">
        <div className="mx-auto">
          {jobPositionId ? <InAppAssessmentReturningConfirmation jobPositionId={jobPositionId} /> : null}
        </div>
      </ModalContent>
    </Modal>
  );
};
